import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_LOGO, fmtIDR, fmtDate, daysUntil } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { rundownStatus, RundownBadge } from "@/lib/rundownStatus";
import { PhotoTransformDialog } from "@/components/PhotoTransformDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LogOut, CalendarDays, MapPin, Bell, FileText, Download, ImagePlus, Trash2, Loader2, Building2, Camera, Play, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const DEFAULT_BG = "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const waLink = (phone) => { let n = String(phone || "").replace(/[^0-9]/g, ""); if (n.startsWith("0")) n = "62" + n.slice(1); return `https://wa.me/${n}`; };
const TL = [["T-180", "Booking vendor utama", 180], ["T-120", "Finalisasi konsep", 120], ["T-90", "Vendor meeting", 90], ["T-60", "Technical meeting", 60], ["T-30", "Final guest count", 30], ["T-14", "Final rundown", 14], ["T-7", "Final confirmation vendor", 7], ["T-1", "Final preparation", 1], ["T-0", "Wedding Day", 0]];
const dayName = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d) ? "" : d.toLocaleDateString("id-ID", { weekday: "long" }); };
const TaskCard = ({ t }) => (
  <div className="border border-gold/20 rounded-xl p-4" data-testid="portal-task">
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-semibold text-[#1A1A1A]">{t.name}</p>
      <StatusBadge status={t.status} />
    </div>
    {t.deadline && <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-2 text-xs text-[#4A4A4A]"><span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-gold-dark" />{fmtDate(t.deadline)}</span>{dayName(t.deadline) && <span>{dayName(t.deadline)}</span>}</div>}
    {(t.pic || t.pic2) && (t.pic ? [{ n: t.pic, p: t.pic_phone }, ...(t.pic2 ? [{ n: t.pic2, p: t.pic2_phone }] : [])] : []).map((pc, i) => (
      <div key={i} className="flex items-center justify-between gap-2 mt-2 bg-beige/40 rounded-lg px-3 py-2">
        <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-gold-dark">PIC</p><p className="text-sm text-[#1A1A1A] truncate">{pc.n}</p>{pc.p && <p className="text-[11px] text-[#7A7A7A]">{pc.p}</p>}</div>
        {pc.p && (
          <div className="flex gap-1.5 shrink-0">
            <a href={waLink(pc.p)} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center text-white" data-testid="portal-task-wa" aria-label="WhatsApp"><MessageCircle className="w-4 h-4" /></a>
            <a href={`tel:${pc.p}`} className="w-8 h-8 rounded-full bg-burgundy hover:bg-burgundy-dark flex items-center justify-center text-white" data-testid="portal-task-call" aria-label="Call"><Phone className="w-4 h-4" /></a>
          </div>
        )}
      </div>
    ))}
  </div>
);

const Section = ({ title, children }) => (
  <div className="card-lux p-5"><h3 className="font-heading text-xl text-burgundy mb-3">{title}</h3>{children}</div>
);

export default function ClientPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [notif, setNotif] = useState([]);
  const [uploading, setUploading] = useState("");
  const [prof, setProf] = useState({ phone: "", address: "", parent_name: "", parent_phone: "" });
  const [savingProf, setSavingProf] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const load = () => api.get("/portal/me").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => {
    load();
    api.get("/portal/notifications").then((r) => setNotif(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (d && d.client) setProf({ phone: d.client.phone || "", address: d.client.address || "", parent_name: d.client.parent_name || "", parent_phone: d.client.parent_phone || "" });
  }, [d]);

  const saveProfile = async () => {
    setSavingProf(true);
    try { await api.put("/portal/profile", prof); toast.success("Profil diperbarui"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan profil"); }
    finally { setSavingProf(false); }
  };
  const uploadAvatar = async (fileList) => {
    const f = (fileList || [])[0]; if (!f) return;
    setUploading("avatar");
    try { const fd = new FormData(); fd.append("file", f); await api.post("/portal/profile/photo", fd); toast.success("Foto profil diperbarui"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah foto"); }
    finally { setUploading(""); }
  };

  const uploadMedia = async (type, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(type);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("type", type); fd.append("caption", "");
        fd.append("file", f);
        await api.post("/portal/media", fd);
      }
      toast.success("Foto ditambahkan ke galeri");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengunggah gambar"); }
    finally { setUploading(""); }
  };
  const delMedia = async (id) => {
    try { await api.delete(`/portal/media/${id}`); toast.success("Foto dihapus"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  // ---- Client hero photo (free transform) ----
  const saveCover = async ({ file, position, zoom }) => {
    if (file) {
      const fd = new FormData();
      fd.append("type", "background"); fd.append("caption", "");
      fd.append("file", file);
      await api.post("/portal/media", fd);
    }
    await api.post("/portal/appearance", { cover_position: position, cover_zoom: zoom });
    toast.success("Foto portal diperbarui");
    await load();
  };
  const deleteCover = async () => {
    const m = (d?.media || []).find((x) => x.type === "background");
    if (m) { await api.delete(`/portal/media/${m.id}`); }
    await api.post("/portal/appearance", { cover_position: "center", cover_zoom: 100 });
    toast.success("Foto dihapus");
    await load();
  };

  if (d === null) return <div className="min-h-screen flex items-center justify-center bg-cream text-[#7A7A7A]">Memuat portal...</div>;
  if (d === false || !d.event) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream gap-4 px-6 text-center">
      <img src={APP_LOGO} alt="Primrose" className="h-24 w-auto" />
      <p className="text-[#7A7A7A]">Belum ada data acara yang tertaut ke akun Anda. Silakan hubungi tim Primrose Organizer.</p>
      <button onClick={logout} className="text-sm text-burgundy underline" data-testid="portal-logout">Keluar</button>
    </div>
  );

  const ev = d.event;
  const cd = daysUntil(ev.wedding_date);
  const contract = Number(ev.total_price) || 0;
  const paid = d.payments.filter((p) => p.status === "PAID").reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const heroSrc = ev.cover ? fileUrl(ev.cover) : DEFAULT_BG;
  const heroZoom = Number(ev.cover_zoom) || 100;

  return (
    <div className="min-h-screen bg-cream">
      <header className="glass-header sticky top-0 z-20 h-16 flex items-center px-5 lg:px-10">
        <img src={APP_LOGO} alt="Primrose Organizer" className="h-10 w-auto object-contain" data-testid="portal-logo" />
        <div className="ml-auto flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 rounded-full hover:bg-beige" data-testid="portal-bell" aria-label="Notifikasi">
                <Bell className="w-5 h-5 text-burgundy" strokeWidth={1.6} />
                {notif.length > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-burgundy text-white text-[9px] font-bold flex items-center justify-center">{notif.length}</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="px-4 py-3 border-b border-gold/20"><h4 className="font-semibold text-sm text-burgundy">Notifikasi</h4></div>
              <div className="max-h-96 overflow-y-auto">
                {notif.length === 0 ? <p className="px-4 py-8 text-center text-sm text-[#7A7A7A]">Belum ada notifikasi.</p> :
                  notif.map((n, i) => (
                    <div key={i} className="px-4 py-3 border-b border-gold/10" data-testid="portal-notif-item">
                      <div className="flex items-start gap-2.5">
                        <span className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", n.level === "OVERDUE" ? "bg-red-500" : n.level === "PENDING" ? "bg-amber-500" : "bg-blue-500")} />
                        <div><p className="text-sm font-medium text-[#1A1A1A]">{n.title}</p><p className="text-xs text-[#7A7A7A] mt-0.5">{n.detail}</p></div>
                      </div>
                    </div>
                  ))}
              </div>
            </PopoverContent>
          </Popover>
          <span className="text-sm text-[#4A4A4A] hidden sm:block">{user?.name}</span>
          <button onClick={logout} data-testid="portal-logout" className="p-2 rounded-lg text-[#7A7A7A] hover:text-burgundy hover:bg-beige"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="card-lux overflow-hidden mb-6 animate-fade-up">
          <div className="relative h-44 sm:h-56 bg-gradient-to-br from-burgundy to-burgundy-dark flex items-end overflow-hidden">
            <img src={heroSrc} alt="foto client" style={{ objectPosition: ev.cover_position || "center", transform: `scale(${heroZoom / 100})` }} className="absolute inset-0 w-full h-full object-cover" data-testid="portal-hero-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              <button onClick={() => setEditorOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/90 hover:bg-white text-burgundy text-xs font-semibold shadow-lg transition-colors" data-testid="portal-change-photo"><Camera className="w-4 h-4" />Ganti Foto</button>
              <button onClick={() => navigate("/portal/event-day")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-burgundy hover:bg-burgundy-dark text-white text-xs font-semibold shadow-lg transition-colors" data-testid="portal-event-day"><Play className="w-4 h-4 text-gold-light" />Event Day Mode</button>
            </div>
            <div className="relative p-6 text-white">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold-light">The Wedding of</p>
              <h1 className="font-heading text-4xl sm:text-5xl leading-none drop-shadow">{ev.groom} & {ev.bride}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/90">
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(ev.wedding_date)}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.venue}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-5">
            <div><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Countdown</p><p className="font-heading text-3xl text-burgundy leading-none mt-1">{cd < 0 ? 0 : cd} <span className="text-base">hari</span></p></div>
            <div className="sm:col-span-2">
              <div className="flex justify-between text-xs mb-1"><span className="text-[#7A7A7A]">Progress Persiapan</span><span className="font-semibold text-burgundy">{d.progress}%</span></div>
              <Progress value={d.progress} className="h-2 bg-beige" />
            </div>
          </div>
        </div>

        <Tabs defaultValue="Persiapan">
          <div className="overflow-x-auto -mx-1 px-1 mb-4">
            <TabsList className="bg-beige inline-flex w-max">
              {["Persiapan", "Rundown", "Timeline", "Vendor", "Galeri", "Pembayaran", "Dokumen", "Profil"].map((t) => <TabsTrigger key={t} value={t} data-testid={`portal-tab-${t.toLowerCase()}`} className="data-[state=active]:bg-burgundy data-[state=active]:text-white">{t}</TabsTrigger>)}
            </TabsList>
          </div>

          <TabsContent value="Persiapan" className="space-y-5">
            {d.pms && d.pms.length > 0 && (
              <Section title="Koordinator / Project Manager Anda">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {d.pms.map((p) => (
                    <div key={p.id} className="flex items-center gap-3" data-testid="portal-pm">
                      {p.photo ? <img src={fileUrl(p.photo)} alt={p.member_name} className="w-12 h-12 rounded-full object-cover border border-gold/30" />
                        : <div className="w-12 h-12 rounded-full bg-beige text-burgundy font-semibold flex items-center justify-center">{(p.member_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{p.member_name}</p>
                        <p className="text-xs text-gold-dark">{p.role}</p>
                        {p.phone && <a href={waLink(p.phone)} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">{p.phone}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Checklist Persiapan">
              <div className="space-y-2">
                {d.checklists.length === 0 && <p className="text-sm text-[#7A7A7A]">Belum ada checklist.</p>}
                {d.checklists.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b border-gold/10 pb-2 last:border-0" data-testid="portal-checklist">
                    <div><p className="text-sm font-medium text-[#1A1A1A]">{c.task}</p><p className="text-[11px] text-[#7A7A7A]">{c.category}</p></div>
                    <StatusBadge status={c.status} />
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Daftar Tugas Tim">
              <div className="space-y-3">
                {d.tasks.length === 0 && <p className="text-sm text-[#7A7A7A]">Belum ada tugas.</p>}
                {d.tasks.map((t) => <TaskCard key={t.id} t={t} />)}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="Rundown">
            <Section title="Rundown Acara">
              <div className="space-y-1">
                {d.rundown.length === 0 && <p className="text-sm text-[#7A7A7A]">Rundown belum tersedia.</p>}
                {d.rundown.map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-3 border-b border-gold/10 py-2.5 last:border-0" data-testid="portal-rundown">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-sm font-semibold text-burgundy whitespace-nowrap">{r.time}{r.end_time ? ` – ${r.end_time}` : ""}</p>
                      <p className="text-sm text-[#1A1A1A] mt-0.5 break-words">{r.activity}</p>
                      {(r.pic || r.location) && <p className="text-[11px] text-[#7A7A7A] mt-0.5">{[r.pic && `PIC: ${r.pic}`, r.location && `Lokasi: ${r.location}`].filter(Boolean).join(" • ")}</p>}
                    </div>
                    <div className="shrink-0">{(() => { const st = rundownStatus(ev, r, now); return st ? <RundownBadge status={st} /> : <StatusBadge status={r.status} />; })()}</div>
                  </div>
                ))}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="Timeline">
            <Section title="Timeline Persiapan">
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gold/40" />
                {(d.timeline && d.timeline.length > 0 ? d.timeline.map((it) => ({ label: it.label, off: Number(it.offset || 0) })) : TL.map(([k, l, o]) => ({ label: l, off: o }))).sort((a, b) => b.off - a.off).map((item, i) => {
                  const target = new Date(new Date(ev.wedding_date).getTime() - item.off * 86400000);
                  const passed = daysUntil(target.toISOString()) < 0;
                  const lab = item.off === 0 ? "T-0" : (item.off > 0 ? `T-${item.off}` : `T+${-item.off}`);
                  return (
                    <div key={i} className="relative flex items-start gap-4" data-testid="portal-timeline">
                      <span className={cn("absolute -left-6 w-4 h-4 rounded-full border-2 mt-0.5", passed ? "bg-burgundy border-burgundy" : "bg-white border-gold")} />
                      <div className="w-14 font-mono text-sm font-semibold text-burgundy shrink-0">{lab}</div>
                      <div><p className="text-sm font-medium text-[#1A1A1A]">{item.label}</p><p className="text-xs text-[#7A7A7A]">{fmtDate(target.toISOString())}</p></div>
                    </div>
                  );
                })}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="Galeri" className="space-y-5">
            <Section title="Galeri Timeline Wedding">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-[#7A7A7A]">Kumpulkan momen & inspirasi perjalanan cinta Anda di sini. Foto ini juga menjadi latar Event Day Mode.</p>
                <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-gold text-burgundy-dark font-semibold cursor-pointer hover:bg-gold-dark shrink-0" data-testid="gallery-timeline-upload">
                  {uploading === "timeline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
                  Tambah Foto
                  <input type="file" accept="image/*" multiple className="hidden" disabled={uploading === "timeline"} onChange={(e) => { uploadMedia("timeline", e.target.files); e.target.value = ""; }} />
                </label>
              </div>
              {(() => {
                const gallery = (d.media || []).filter((m) => m.type === "timeline");
                if (gallery.length === 0) return <p className="text-sm text-[#7A7A7A] text-center py-8" data-testid="gallery-empty">Belum ada foto. Unggah foto pertama Anda 🌸</p>;
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {gallery.map((m) => (
                      <div key={m.id} className="relative group rounded-xl overflow-hidden aspect-square bg-beige border border-gold/20" data-testid="gallery-item">
                        <img src={fileUrl(m.storage_path)} alt={m.caption || "foto"} className="w-full h-full object-cover" />
                        <button onClick={() => delMedia(m.id)} data-testid="gallery-delete" className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Section>
          </TabsContent>

          <TabsContent value="Pembayaran" className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="card-lux p-4"><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Total</p><p className="font-heading text-lg text-[#1A1A1A] mt-0.5">{fmtIDR(contract)}</p></div>
              <div className="card-lux p-4"><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Dibayar</p><p className="font-heading text-lg text-emerald-600 mt-0.5">{fmtIDR(paid)}</p></div>
              <div className="card-lux p-4"><p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Sisa</p><p className="font-heading text-lg text-amber-600 mt-0.5">{fmtIDR(Math.max(contract - paid, 0))}</p></div>
            </div>
            <Section title="Termin Pembayaran">
              <div className="space-y-2">
                {d.payments.length === 0 && <p className="text-sm text-[#7A7A7A]">Belum ada data pembayaran.</p>}
                {d.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 border-b border-gold/10 pb-2 last:border-0" data-testid="portal-payment">
                    <span className="font-mono text-sm font-semibold text-burgundy w-28">{fmtIDR(p.amount)}</span>
                    <div className="flex-1"><p className="text-sm font-medium text-[#1A1A1A]">{p.label}</p><p className="text-[11px] text-[#7A7A7A]">Jatuh tempo {fmtDate(p.due_date)}</p></div>
                    <StatusBadge status={p.status} />
                  </div>
                ))}
              </div>
            </Section>
            {d.invoices.length > 0 && (
              <Section title="Invoice">
                {d.invoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between border-b border-gold/10 pb-2 last:border-0" data-testid="portal-invoice">
                    <div><p className="font-mono text-sm text-burgundy font-semibold">{inv.invoice_number}</p><p className="text-[11px] text-[#7A7A7A]">Sisa {fmtIDR(inv.remaining)}</p></div>
                    <StatusBadge status={inv.status} />
                  </div>
                ))}
              </Section>
            )}
          </TabsContent>

          <TabsContent value="Vendor">
            <Section title="Vendor Acara Anda">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(!d.vendors || d.vendors.length === 0) && <p className="text-sm text-[#7A7A7A]">Vendor akan ditampilkan setelah tim Primrose menetapkannya.</p>}
                {(d.vendors || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-3 border border-gold/20 rounded-xl p-3" data-testid="portal-vendor">
                    <div className="w-10 h-10 rounded-full bg-beige text-burgundy flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" strokeWidth={1.6} /></div>
                    <div className="min-w-0">
                      {v.category ? <p className="text-[11px] uppercase tracking-wider text-gold-dark">{v.category}</p> : null}
                      <p className="text-sm font-semibold text-[#1A1A1A] truncate">{v.vendor_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="Dokumen">
            <Section title="Dokumen Anda">
              <div className="space-y-2">
                {(!d.documents || d.documents.length === 0) && <p className="text-sm text-[#7A7A7A]">Belum ada dokumen. Tim Primrose akan mengunggah kontrak & invoice Anda di sini.</p>}
                {(d.documents || []).map((doc) => {
                  const href = doc.file ? fileUrl(doc.file) : (doc.url || "");
                  return (
                    <div key={doc.id} className="flex items-center gap-3 border-b border-gold/10 pb-2 last:border-0" data-testid="portal-document">
                      <FileText className="w-5 h-5 text-burgundy shrink-0" />
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#1A1A1A] truncate">{doc.name}</p><p className="text-[11px] text-gold-dark">{doc.type}</p></div>
                      {href ? <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-burgundy text-white shrink-0" data-testid="portal-doc-download"><Download className="w-3 h-3" />Unduh</a> : <span className="text-xs text-[#9A9A9A]">Tidak tersedia</span>}
                    </div>
                  );
                })}
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="Profil">
            <Section title="Profil Saya">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex flex-col items-center gap-3 shrink-0">
                  {d.client.photo ? <img src={fileUrl(d.client.photo)} alt="foto profil" className="w-28 h-28 rounded-full object-cover border-2 border-gold/40" data-testid="profile-photo" />
                    : <div className="w-28 h-28 rounded-full bg-beige text-burgundy font-heading text-3xl flex items-center justify-center" data-testid="profile-photo">{(d.client.groom || "?")[0]}{(d.client.bride || "?")[0]}</div>}
                  <label className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-burgundy text-white cursor-pointer hover:bg-burgundy-dark" data-testid="profile-photo-upload">
                    {uploading === "avatar" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}Ubah Foto
                    <input type="file" accept="image/*" className="hidden" disabled={uploading === "avatar"} onChange={(e) => { uploadAvatar(e.target.files); e.target.value = ""; }} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label className="text-xs font-semibold text-gold-dark mb-1 block">Pengantin</Label><Input value={`${d.client.groom} & ${d.client.bride}`} disabled className="bg-beige/40" /></div>
                  <div className="sm:col-span-2"><Label className="text-xs font-semibold text-gold-dark mb-1 block">Email (login)</Label><Input value={d.client.email || user?.email || ""} disabled className="bg-beige/40" /></div>
                  <div><Label className="text-xs font-semibold text-gold-dark mb-1 block">No. WhatsApp</Label><Input value={prof.phone} onChange={(e) => setProf({ ...prof, phone: e.target.value })} data-testid="profile-phone" /></div>
                  <div><Label className="text-xs font-semibold text-gold-dark mb-1 block">Alamat</Label><Input value={prof.address} onChange={(e) => setProf({ ...prof, address: e.target.value })} data-testid="profile-address" /></div>
                  <div><Label className="text-xs font-semibold text-gold-dark mb-1 block">Nama Orang Tua</Label><Input value={prof.parent_name} onChange={(e) => setProf({ ...prof, parent_name: e.target.value })} data-testid="profile-parent-name" /></div>
                  <div><Label className="text-xs font-semibold text-gold-dark mb-1 block">Kontak Orang Tua</Label><Input value={prof.parent_phone} onChange={(e) => setProf({ ...prof, parent_phone: e.target.value })} data-testid="profile-parent-phone" /></div>
                  <div className="sm:col-span-2"><Button onClick={saveProfile} disabled={savingProf} data-testid="profile-save" className="bg-burgundy hover:bg-burgundy-dark text-white rounded-full">{savingProf ? "Menyimpan..." : "Simpan Profil"}</Button></div>
                </div>
              </div>
            </Section>
          </TabsContent>
        </Tabs>
      </main>

      <PhotoTransformDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        currentSrc={ev.cover ? fileUrl(ev.cover) : null}
        hasPhoto={Boolean(ev.cover)}
        initialPosition={ev.cover_position}
        initialZoom={ev.cover_zoom}
        onSave={saveCover}
        onDelete={deleteCover}
      />
    </div>
  );
}
